import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { BRAND } from "@/lib/brand";

const sectionLinks = [
  { anchor: "story", label: "Our Story" },
  { anchor: "designer", label: "AI Designer" },
  { anchor: "testimonials", label: "Reviews" },
  { anchor: "order", label: "Order" },
  { anchor: "contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  const hrefFor = (anchor) => (onLanding ? `#${anchor}` : `/#${anchor}`);
  const logoHref = onLanding ? "#top" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ...sectionLinks.slice(0, 2),
    { anchor: "__gallery__", label: "Gallery", href: "/gallery" },
    ...sectionLinks.slice(2),
  ];

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#EFE8E8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <a href={logoHref} className="flex items-baseline gap-2" data-testid="nav-logo">
          <span className="font-brand-script text-[34px] md:text-[44px] text-[#B37C7C]">
            Sweet Rose
          </span>
          <span className="font-brand-script text-[34px] md:text-[44px] text-[#B37C7C]">
            Cookies
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.anchor}
              href={l.href || hrefFor(l.anchor)}
              className="text-sm tracking-wide text-[#5C5252] hover:text-[#B37C7C] transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href={hrefFor("order")}
          className="hidden md:inline-flex items-center bg-[#B37C7C] text-white px-5 py-2 rounded-md text-sm hover:bg-[#9A6565] hover:-translate-y-0.5 hover:shadow-lg shadow-[#B37C7C]/20 transition-all duration-300 tracking-wide"
          data-testid="nav-cta-order"
        >
          Request a Quote
        </a>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-[#362C2C]"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#EFE8E8]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.anchor}
                href={l.href || hrefFor(l.anchor)}
                onClick={() => setOpen(false)}
                className="text-[#5C5252] text-base"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={hrefFor("order")}
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center bg-[#B37C7C] text-white px-5 py-2.5 rounded-md text-sm tracking-wide"
              data-testid="nav-mobile-cta"
            >
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
